import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Download, ZoomIn, ZoomOut, X, ChevronUp, ChevronDown } from 'lucide-react';
import { documentsApi } from '../lib/api';
import { formatFileSize } from '../lib/utils';
import { toast } from 'sonner';

/**
 * Decryption steps, driven by real state (see DECRYPT_STAGE_* below) rather
 * than a fixed timer — a large file's fetch+decrypt can take well over the
 * couple of seconds the old fixed dwell times assumed, and once those timers
 * finished the checklist showed every step ticked off with no spinner while
 * the real work kept going for another 20-30s, which looked exactly like a
 * hang. Fetch and unlock happen together server-side in one request (the
 * server decrypts with the key we send it), so they're one honest step.
 */
const DECRYPT_STAGE_FETCHING = 0;
const DECRYPT_STAGE_PREPARING = 1;
const DECRYPT_STEPS = [
  { id: 'fetch',    label: 'Fetching and unlocking your document' },
  { id: 'prepare',  label: 'Preparing the preview' },
];
// Below this, a large/slow file gets an extra reassurance line instead of
// silently sitting on the same step.
const SLOW_HINT_MS = 6000;

const DecryptingOverlay = ({ activeStep, elapsedMs }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
    <h3 className="font-heading text-[22px] font-semibold tracking-[-0.02em] mb-2">Unlocking your document</h3>
    <p className="text-sm text-ink-55 mb-6 max-w-sm">
      This file is encrypted. Only your key can open it.
    </p>

    <div className="w-full max-w-sm space-y-2 text-left bg-ground rounded-lg p-4" aria-live="polite">
      {DECRYPT_STEPS.map((s, i) => {
        const done = i < activeStep;
        const current = i === activeStep;
        return (
          <div key={s.id} className={`flex items-center gap-3 ${!done && !current ? 'opacity-50' : ''}`}>
            <span aria-hidden="true" className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${done ? 'bg-brand' : 'bg-shape'}`} />
            <span className="text-sm text-ink-70 flex-1">{s.label}</span>
            {current && <Loader2 className="w-3.5 h-3.5 text-ink-55 animate-spin" aria-hidden="true" />}
          </div>
        );
      })}
    </div>
    {activeStep === DECRYPT_STAGE_FETCHING && elapsedMs > SLOW_HINT_MS && (
      <p className="text-sm text-ink-55 mt-4" data-testid="doc-viewer-slow-hint">
        Still working — larger files can take up to a minute.
      </p>
    )}
  </div>
);

const NATIVE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif'];

/**
 * Whether we can tell, from the list metadata alone, that this document is a
 * PDF or image (handled by the browser natively). Anything else — or a doc
 * whose type is only inside its encrypted metadata, as with email
 * attachments — goes through the paged text preview first, which tells us
 * if it's native after all.
 */
const looksNative = (doc) => {
  const mime = (doc?.content_type || '').split(';')[0].trim().toLowerCase();
  if (mime === 'application/pdf' || mime.startsWith('image/')) return true;
  const name = doc?.original_filename || '';
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  return NATIVE_EXTENSIONS.includes(ext);
};

const PAGE_LABELS = { page: 'Page', slide: 'Slide', sheet: 'Sheet' };

/**
 * Paged text view for converted documents (Word, PowerPoint, Excel, email,
 * plain text). The pages are the same synthetic split the analysis
 * pipeline used, so `initialPage` from a record's source lands on the text
 * the fact was read from.
 */
const PagedTextPreview = ({ pages, pageLabel, sourceFormat, truncated, initialPage }) => {
  const containerRef = useRef(null);
  const pageRefs = useRef([]);
  const [current, setCurrent] = useState(() => {
    const n = Number(initialPage) || 1;
    return Math.min(Math.max(n, 1), pages.length);
  });
  const label = PAGE_LABELS[pageLabel] || 'Page';

  const scrollTo = (n) => {
    const clamped = Math.min(Math.max(n, 1), pages.length);
    const el = pageRefs.current[clamped - 1];
    if (el && containerRef.current) {
      containerRef.current.scrollTo({ top: el.offsetTop - containerRef.current.offsetTop, behavior: 'smooth' });
    }
    setCurrent(clamped);
  };

  // Jump once on mount; smooth scrolling would fight with the dialog's
  // opening animation, so this first one is instant.
  useEffect(() => {
    const n = Math.min(Math.max(Number(initialPage) || 1, 1), pages.length);
    const el = pageRefs.current[n - 1];
    if (n > 1 && el && containerRef.current) {
      containerRef.current.scrollTop = el.offsetTop - containerRef.current.offsetTop;
    }
  }, [initialPage, pages.length]);

  // Keep the "Page N of M" readout honest as the user scrolls.
  const handleScroll = () => {
    const c = containerRef.current;
    if (!c) return;
    const probe = c.scrollTop + c.clientHeight * 0.3;
    let best = 1;
    pageRefs.current.forEach((el, i) => {
      if (el && el.offsetTop - c.offsetTop <= probe) best = i + 1;
    });
    setCurrent(best);
  };

  return (
    <div data-testid="doc-viewer-pages">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="text-sm text-ink-55">
          {sourceFormat ? `Converted from .${sourceFormat} for reading` : 'Text preview'}
          {truncated && ' · very long file, showing the first part only'}
        </p>
        {pages.length > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => scrollTo(current - 1)}
              disabled={current <= 1}
              aria-label={`Previous ${label.toLowerCase()}`}
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <span className="pd-mono text-ink-70 min-w-[7rem] text-center" data-testid="doc-viewer-page-indicator">
              {label} {current} of {pages.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => scrollTo(current + 1)}
              disabled={current >= pages.length}
              aria-label={`Next ${label.toLowerCase()}`}
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="relative overflow-auto max-h-[70vh] space-y-3 pr-1"
      >
        {pages.map((text, i) => (
          <section
            key={i}
            ref={(el) => { pageRefs.current[i] = el; }}
            id={`preview-page-${i + 1}`}
            aria-label={`${label} ${i + 1}`}
            className={`rounded-lg border bg-ground p-4 ${
              i + 1 === Number(initialPage) ? 'border-brand' : 'border-rule'
            }`}
            data-testid={`doc-viewer-page-${i + 1}`}
          >
            {pages.length > 1 && (
              <p className="pd-mono text-ink-55 mb-2">{label} {i + 1}</p>
            )}
            <pre className="text-sm whitespace-pre-wrap font-mono text-ink">
              {text || <span className="text-ink-55">(empty {label.toLowerCase()})</span>}
            </pre>
          </section>
        ))}
      </div>
    </div>
  );
};

/**
 * In-browser document lightbox with theatrical decryption sequence.
 */
export const DocumentViewer = ({ doc, open, onOpenChange, initialPage, onViewed }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [filename, setFilename] = useState(null);
  const [size, setSize] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [pagedPreview, setPagedPreview] = useState(null);
  const [unsupportedReason, setUnsupportedReason] = useState(null);
  const [loading, setLoading] = useState(false);
  const [decryptStep, setDecryptStep] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const cachedBlobRef = useRef(null);

  // Pulled out so the effect depends on these scalars, not on the `doc`
  // object identity (which changes every parent render).
  const docId = doc?.id;
  const docName = doc?.original_filename;
  const docMime = doc?.content_type;

  useEffect(() => {
    if (!open || !docId) return;

    let cancelled = false;
    let createdUrl = null;

    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setTextContent(null);
    setPagedPreview(null);
    setUnsupportedReason(null);
    setImageZoom(1);
    setDecryptStep(DECRYPT_STAGE_FETCHING);
    setElapsedMs(0);
    cachedBlobRef.current = null;

    const startTs = Date.now();
    const tick = setInterval(() => {
      if (!cancelled) setElapsedMs(Date.now() - startTs);
    }, 1000);

    // They've actually seen it now — mark it read (best-effort, never blocks
    // the viewer). Distinct from `analysis_status`: this is a person opening
    // the raw file themselves, not the AI pipeline.
    const markReadOnce = () => {
      documentsApi.markViewed(docId)
        .then((res) => { if (res.data?.newly_read) onViewed?.(docId); })
        .catch(() => { /* best-effort */ });
    };

    const loadNative = async () => {
      const response = await documentsApi.download(docId);
      if (cancelled) return;

      const blob = response.data;
      cachedBlobRef.current = blob;
      // The server has already fetched and decrypted it — from here it's
      // genuinely fast client-side work, so this step is real too, not
      // padded.
      setDecryptStep(DECRYPT_STAGE_PREPARING);

      const headerType = response.headers?.['content-type'] || blob.type || 'application/octet-stream';
      const mime = headerType.split(';')[0].trim();

      const disposition = response.headers?.['content-disposition'] || '';
      let parsed = docName;
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      if (match) parsed = match[1];

      setFilename(parsed || 'document');
      setContentType(mime);
      setSize(blob.size);

      if (mime.startsWith('text/') || mime === 'application/json' || mime === 'application/xml') {
        const text = await blob.text();
        if (cancelled) return;
        setTextContent(text);
      } else {
        createdUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(createdUrl);
          return;
        }
        // #page=N is honoured by the browser's native PDF viewer even on
        // a blob: URL — appended only for display, revoke() below still
        // uses the bare createdUrl it was given.
        const jumpTo = mime === 'application/pdf' && initialPage ? `#page=${initialPage}` : '';
        setBlobUrl(createdUrl + jumpTo);
      }
      markReadOnce();
    };

    (async () => {
      try {
        if (looksNative({ content_type: docMime, original_filename: docName })) {
          await loadNative();
          return;
        }
        // Office / email / text: ask for the paged text version. The server
        // converts the decrypted original in memory — nothing derived is
        // stored — and tells us if it was a PDF/image after all.
        const { data } = await documentsApi.previewPages(docId);
        if (cancelled) return;
        if (data.kind === 'native') {
          await loadNative();
          return;
        }
        setDecryptStep(DECRYPT_STAGE_PREPARING);
        setFilename(data.filename || docName || 'document');
        setContentType(data.content_type || null);
        setSize(data.size || null);
        if (data.kind === 'pages') {
          setPagedPreview({
            pages: data.pages || [],
            pageLabel: data.page_label,
            sourceFormat: data.source_format,
            truncated: !!data.truncated,
          });
        } else {
          setUnsupportedReason(data.reason || null);
        }
        markReadOnce();
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || err?.message || 'The document could not be loaded.';
        const hint = msg.toLowerCase().includes('encryption key not found')
          ? 'Your session has expired. Sign out and back in.'
          : msg;
        setError(hint);
      } finally {
        if (!cancelled) setLoading(false);
        clearInterval(tick);
      }
    })();

    return () => {
      cancelled = true;
      clearInterval(tick);
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // onViewed deliberately excluded — an inline callback from the parent
    // would otherwise re-run this whole load on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docId, docName, docMime, initialPage]);

  const handleDownload = async () => {
    try {
      const blob = cachedBlobRef.current || (await documentsApi.download(doc.id)).data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || doc.original_filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('The download did not go through.');
    }
  };

  const renderPreview = () => {
    if (loading) {
      return <DecryptingOverlay activeStep={decryptStep} elapsedMs={elapsedMs} />;
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="font-medium mb-1 text-ink">The preview could not be loaded</p>
          <p className="text-sm text-breach-ink">{error}</p>
        </div>
      );
    }

    if (pagedPreview) {
      return (
        <PagedTextPreview
          pages={pagedPreview.pages}
          pageLabel={pagedPreview.pageLabel}
          sourceFormat={pagedPreview.sourceFormat}
          truncated={pagedPreview.truncated}
          initialPage={initialPage}
        />
      );
    }

    if (textContent !== null) {
      return (
        <pre
          className="text-sm bg-ground p-4 rounded-lg overflow-auto max-h-[70vh] whitespace-pre-wrap font-mono text-ink"
          data-testid="doc-viewer-text"
        >
          {textContent}
        </pre>
      );
    }

    if (unsupportedReason) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mb-4" />
          <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] mb-1">No preview for this file</p>
          <p className="text-base text-ink-55 mb-5 max-w-md" data-testid="doc-viewer-unsupported-reason">
            {unsupportedReason}
          </p>
          <Button variant="secondary" onClick={handleDownload}>
            Download to view
          </Button>
        </div>
      );
    }

    if (!blobUrl) return null;

    if (contentType?.startsWith('image/')) {
      return (
        <div className="rounded-lg overflow-auto max-h-[70vh] flex items-center justify-center p-2 bg-ground">
          <img
            src={blobUrl}
            alt={filename}
            style={{ transform: `scale(${imageZoom})`, transformOrigin: 'center', transition: 'transform 0.15s ease' }}
            className="max-w-full select-none"
            data-testid="doc-viewer-image"
          />
        </div>
      );
    }

    if (contentType === 'application/pdf') {
      return (
        <iframe
          src={blobUrl}
          title={filename}
          className="w-full h-[75vh] rounded-lg bg-ground"
          data-testid="doc-viewer-pdf"
        />
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mb-4" />
        <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] mb-1">No preview for this file</p>
        <p className="text-base text-ink-55 mb-5">
          This file type ({contentType || 'unknown'}) cannot be shown here. Download it to open it on your device.
        </p>
        <Button variant="secondary" onClick={handleDownload}>
          Download to view
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 gap-0 overflow-hidden [&>button.absolute]:hidden"
        data-testid="doc-viewer-dialog"
      >
        <DialogHeader className="px-4 sm:px-5 py-3 sm:py-4 border-b border-rule">
          <div className="flex items-start justify-between gap-2 sm:gap-3">
            <div className="min-w-0 flex-1 pr-1">
              <DialogTitle className="font-heading text-base sm:text-[19px] font-semibold tracking-[-0.02em] truncate text-left">
                {filename || doc?.original_filename || 'Document'}
              </DialogTitle>
              <DialogDescription className="text-sm text-ink-55 text-left">
                {contentType ? `${contentType} · ` : ''}
                {size ? formatFileSize(size) : (doc?.file_size ? formatFileSize(doc.file_size) : '')}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {contentType?.startsWith('image/') && blobUrl && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setImageZoom(z => Math.max(0.5, z - 0.25))}
                    className="hidden sm:inline-flex"
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setImageZoom(z => Math.min(3, z + 0.25))}
                    className="hidden sm:inline-flex"
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                </>
              )}
              {/* Download — icon-only on mobile, labelled on desktop */}
              <Button
                variant="secondary"
                size="icon"
                onClick={handleDownload}
                disabled={loading || !!error}
                className="sm:hidden"
                aria-label="Download"
                data-testid="doc-viewer-download-btn-mobile"
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                disabled={loading || !!error}
                className="hidden sm:inline-flex"
                data-testid="doc-viewer-download-btn"
              >
                Download
              </Button>
              {/* Close — icon-only on mobile (always visible), labelled on desktop */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="sm:hidden"
                aria-label="Close"
                data-testid="doc-viewer-close-btn-mobile"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="hidden sm:inline-flex"
                data-testid="doc-viewer-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="p-4">{renderPreview()}</div>

        {/* Encryption audit trail */}
        {!loading && !error && (
          <div className="px-5 py-3 border-t border-rule bg-ground pd-mono text-ink-55">
            <span data-testid="doc-viewer-audit-trail">
              Decrypted at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} today
              {doc?.uploaded_at && (
                <>
                  {' · '}encrypted at upload on {new Date(doc.uploaded_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                </>
              )}
              {' · '}RSA-4096 + AES-256
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;
